import { useState } from 'preact/hooks';
import { createTip, updateTip } from '../../api/tips';
import { Card } from '../../components/card';
import { Textarea } from '../../components/textarea';
import { Select } from '../../components/select';
import { Button } from '../../components/button';
import { Message } from '../../components/message';
import type { Tip } from '../../api/types';

interface TipFormProps {
  tip?: Tip;
  onSave: () => void;
  onCancel: () => void;
}

const FRAMEWORK_OPTIONS = [
  { value: '', label: 'General (no framework)' },
  { value: 'halal', label: 'Halal' },
  { value: 'allergen', label: 'Allergen' },
  { value: 'diabetic', label: 'Diabetic' },
  { value: 'vegan', label: 'Vegan' },
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'heart-healthy', label: 'Heart-healthy' },
  { value: 'keto', label: 'Keto' },
  { value: 'renal', label: 'Renal' },
];

export function TipForm({ tip, onSave, onCancel }: TipFormProps) {
  const isEdit = !!tip;
  const [text, setText] = useState(tip?.text ?? '');
  const [frameworkId, setFrameworkId] = useState(tip?.framework_id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!text.trim()) {
      setError('Tip text is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const fw = frameworkId || null;
      if (isEdit) {
        await updateTip(tip!.id, { text: text.trim(), framework_id: fw });
      } else {
        await createTip({ text: text.trim(), framework_id: fw });
      }
      onSave();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <h2>{isEdit ? 'Edit Tip' : 'Add Tip'}</h2>

      {error && <Message text={error} type="error" />}

      <div style={{ marginBottom: '12px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Tip Text</label>
        <Textarea value={text} onChange={setText} rows={3} placeholder="Enter tip text..." />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.85rem' }}>Framework</label>
        <Select value={frameworkId} onChange={setFrameworkId} options={FRAMEWORK_OPTIONS} />
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </Card>
  );
}
